import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowUp, ExternalLink, Syringe, Shield, Flame, Clock, AlertTriangle, Check, Beaker, Heart, Target, Info } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const ARTICLE_SLUG = 'the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'what', label: 'What Is It' },
  { id: 'bpc157', label: 'BPC-157' },
  { id: 'tb500', label: 'TB-500' },
  { id: 'synergy', label: 'Synergy' },
  { id: 'protocol', label: 'Protocol' },
  { id: 'sideeffects', label: 'Side Effects' },
  { id: 'stacking', label: 'Stacking' },
  { id: 'faq', label: 'FAQ' },
];

const PEPTIDE_DATA = {
  bpc157: {
    name: 'BPC-157',
    fullName: 'Body Protection Compound-157',
    icon: Shield,
    color: '#22c55e',
    origin: 'Derived from a protein found in human gastric juice',
    mechanism: 'Promotes angiogenesis (new blood vessel formation), modulates nitric oxide, and accelerates tissue repair at the injury site.',
    benefits: [
      'Tendon and ligament repair',
      'Gut healing (leaky gut, IBS, ulcers)',
      'Muscle tear recovery',
      'Anti-inflammatory effects',
      'Neuroprotective properties',
      'Counteracts NSAID damage',
    ],
    dosage: '250-500mcg subcutaneous, 2x/day',
    timing: 'Inject as close to injury site as possible. Morning and evening.',
    cycle: '4-8 weeks on, 2-4 weeks off',
    research: 'Over 100 studies demonstrate tissue-protective and healing properties. Studied extensively for GI healing, tendon repair, and wound healing in animal models.',
    keyStudy: 'Sikiric et al. (2018) showed BPC-157 accelerated tendon-to-bone healing in rats by 2x compared to controls.',
  },
  tb500: {
    name: 'TB-500',
    fullName: 'Thymosin Beta-4 Fragment',
    icon: Heart,
    color: '#3b82f6',
    origin: 'Synthetic version of a naturally occurring peptide in nearly all human cells',
    mechanism: 'Upregulates actin, promoting cell migration, blood vessel growth, and reducing inflammation systemically throughout the body.',
    benefits: [
      'Systemic tissue repair (whole body)',
      'Cardiac tissue protection',
      'Reduced inflammation',
      'Improved flexibility',
      'Hair regrowth (reported)',
      'Faster wound healing',
    ],
    dosage: '2-2.5mg subcutaneous, 2x/week',
    timing: 'Any time of day. Abdomen or deltoid injection.',
    cycle: '4-6 week loading phase, then maintenance',
    research: 'Research shows TB-500 promotes cell survival, reduces inflammation, and enhances wound healing. Studied in cardiac repair and dermal wound models.',
    keyStudy: 'Sosne et al. (2007) demonstrated Thymosin Beta-4 accelerated corneal wound healing by 40% in clinical trials.',
  },
};

const PROTOCOL_PHASES = [
  { phase: 'Loading (Weeks 1-2)', bpc: '500mcg 2x/day', tb: '2.5mg 2x/week', note: 'Higher doses to kickstart healing response' },
  { phase: 'Therapeutic (Weeks 3-6)', bpc: '250mcg 2x/day', tb: '2mg 2x/week', note: 'Sustained healing, monitor response' },
  { phase: 'Maintenance (Weeks 7-8)', bpc: '250mcg 1x/day', tb: '2mg 1x/week', note: 'Taper down, assess results' },
  { phase: 'Off-Cycle (2-4 weeks)', bpc: 'None', tb: 'None', note: 'Allow body to normalize, get bloodwork' },
];

const STACK_OPTIONS = [
  { name: 'Recovery Max', add: 'GHK-Cu (200mcg/day)', why: 'Adds skin and tissue repair plus neuroprotection. Triple-threat healing stack.', link: '/blogs/article/ghk-cu-copper-peptide-the-complete-master-class-guide' },
  { name: 'Longevity Stack', add: 'MOTS-c (5mg 3x/week)', why: 'Mitochondrial optimization plus tissue repair. Covers both cellular energy and structural healing.', link: '/blogs/article/mots-c-vs-metformin-longevity-comparison' },
  { name: 'Body Recomp', add: 'CJC-1295/Ipa + AOD-9604', why: 'Growth hormone release for recovery, sleep, and fat loss while healing.', link: '/blogs/article/aod-9604-fat-burning-peptide-guide' },
  { name: 'Brain + Body', add: 'Dihexa (nasal, 5mg/week)', why: 'Neurotrophic support alongside physical repair. Full-spectrum optimization.', link: '/blogs/article/dihexa-peptide-brain-repair-what-the-science-actually-says' },
];

const FAQS = [
  { q: 'How long until I see results?', a: 'Most users notice reduced pain and improved mobility within 1-2 weeks. Significant healing improvements typically occur by weeks 3-4. Full tissue remodeling can take 6-8 weeks.' },
  { q: 'Can I inject BPC-157 and TB-500 at the same time?', a: 'Yes. You can mix them in the same syringe or inject separately. Many users inject BPC-157 locally (near injury site) and TB-500 subcutaneously in the abdomen for systemic effects.' },
  { q: 'Do I need bloodwork before starting?', a: 'Strongly recommended. Get a baseline CBC, CMP, and inflammatory markers (CRP, ESR). This lets you objectively measure the protocol\'s impact and catch any issues early.' },
  { q: 'Are there side effects?', a: 'BPC-157 and TB-500 are generally well-tolerated. Common mild effects include injection site redness, mild nausea (BPC-157), and lightheadedness. Serious side effects are rare in research literature.' },
  { q: 'Where do I buy quality peptides?', a: 'Only purchase from vendors who provide third-party Certificate of Analysis (COA) with purity testing. Avoid any source that doesn\'t provide batch-specific lab results. Purity should be 98%+ minimum.' },
  { q: 'Can I use the Wolverine Complex with red light therapy?', a: 'Absolutely. Red light therapy (photobiomodulation) enhances blood flow and cellular energy production at the injury site, potentially accelerating peptide delivery and effectiveness.' },
];

const RELATED = [
  { title: 'BPC-157 Dosage Guide', href: '/blogs/article/bpc-157-dosage-guide' },
  { title: 'How to Reconstitute Peptides', href: '/blogs/article/how-to-reconstitute-peptides' },
  { title: 'Blood Tests Before Peptides', href: '/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide' },
  { title: 'Peptide Side Effects Guide', href: '/blogs/article/peptide-side-effects-what-the-research-actually-says' },
  { title: 'Red Light Panel Selection Guide', href: '/blogs/article/how-to-select-the-correct-size-red-light-panel' },
  { title: 'Complete Peptide Therapy Guide', href: '/blogs/article/the-complete-guide-to-peptide-therapy-for-athletes' },
];

function PeptideCard({ data, isOpen, onToggle }) {
  const Icon = data.icon;
  return (
    <div className="border border-zinc-800 overflow-hidden" data-testid={`peptide-card-${data.name.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
      <button onClick={onToggle} className="w-full p-5 flex items-center gap-4 text-left cursor-pointer">
        <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${data.color}15`, border: `1px solid ${data.color}30` }}>
          <Icon className="w-6 h-6" style={{ color: data.color }} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-heading text-white">{data.name}</h3>
          <p className="text-xs text-zinc-500">{data.fullName}</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#c41e3a]' : ''}`} />
      </button>
      <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-5 space-y-4">
          <p className="text-xs text-zinc-500 italic">{data.origin}</p>
          <div>
            <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-2">Mechanism</p>
            <p className="text-sm text-zinc-300 leading-relaxed">{data.mechanism}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-2">Key Benefits</p>
            <div className="grid grid-cols-2 gap-1.5">
              {data.benefits.map((b, i) => (
                <div key={b} className="flex items-center gap-2 text-xs text-zinc-400">
                  <Check className="w-3 h-3 flex-shrink-0" style={{ color: data.color }} /> {b}
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-900 p-3"><p className="text-[10px] text-zinc-500 mb-1">Dosage</p><p className="text-xs text-white font-semibold">{data.dosage}</p></div>
            <div className="bg-zinc-900 p-3"><p className="text-[10px] text-zinc-500 mb-1">Timing</p><p className="text-xs text-white font-semibold">{data.timing}</p></div>
            <div className="bg-zinc-900 p-3"><p className="text-[10px] text-zinc-500 mb-1">Cycle</p><p className="text-xs text-white font-semibold">{data.cycle}</p></div>
          </div>
          <div className="bg-blue-950/20 border-l-2 border-blue-500/50 p-3">
            <p className="text-xs text-blue-200/80"><strong className="text-blue-200">Research:</strong> {data.research}</p>
            <p className="text-xs text-blue-300/60 mt-1 italic">{data.keyStudy}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-zinc-800" data-testid="wolverine-faq-item">
      <button onClick={onToggle} className="w-full py-4 flex items-center justify-between text-left group cursor-pointer">
        <span className="text-sm font-semibold text-white group-hover:text-[#c41e3a] transition-colors pr-4">{faq.q}</span>
        <ChevronDown className={`w-4 h-4 text-[#c41e3a] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

export default function WolverineComplexArticle() {
  const [activeNav, setActiveNav] = useState('overview');
  const [openCards, setOpenCards] = useState({ bpc157: true });
  const [openFaq, setOpenFaq] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);

  const handleScroll = useCallback(() => {
    const h = document.documentElement;
    const pct = Math.min((h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100, 100);
    setProgress(pct);
    setShowTop(h.scrollTop > 500);
    setProgressForSlug(ARTICLE_SLUG, pct);
    const offsets = NAV_ITEMS.map(n => {
      const el = document.getElementById(`wc-${n.id}`);
      return el ? { id: n.id, top: el.getBoundingClientRect().top } : null;
    }).filter(Boolean);
    const current = offsets.filter(o => o.top < 150).pop();
    if (current) setActiveNav(current.id);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => document.getElementById(`wc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="wolverine-complex-article">
      <div className="fixed top-0 left-0 h-[3px] bg-[#c41e3a] z-[100] transition-all duration-100" style={{ width: `${progress}%` }} data-testid="reading-progress" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_80%_50%,rgba(196,30,58,0.06)_0%,transparent_70%)]" />
        <div className="max-w-3xl mx-auto relative z-10">
          <span className="inline-block text-[10px] font-mono tracking-[0.12em] uppercase text-[#c41e3a] border border-[#c41e3a]/30 px-3 py-1 mb-6">Recovery Protocol · Peptides · 2026</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-6" data-testid="article-title">
            THE WOLVERINE COMPLEX:<br/><span className="text-[#c41e3a]">BPC-157 + TB-500</span>
          </h1>
          <p className="text-lg text-zinc-300 max-w-xl leading-relaxed mb-8">The ultimate peptide stack for rapid recovery and healing. How two peptides work synergistically to accelerate tissue repair, reduce inflammation, and get you back to full performance.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-zinc-400">
            <span>Updated 2026</span><span>12 min read</span><span>Research-backed</span>
          </div>
        </div>
      </section>

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-zinc-800/50" data-testid="article-sticky-nav">
        <div className="max-w-3xl mx-auto flex overflow-x-auto scrollbar-hide px-4">
          {NAV_ITEMS.map(n => (
            <button key={n.id} onClick={() => scrollTo(n.id)}
              className={`whitespace-nowrap px-4 py-3 text-[10px] sm:text-[11px] font-mono tracking-wider uppercase border-b-2 transition-colors flex-shrink-0 ${activeNav === n.id ? 'text-[#c41e3a] border-[#c41e3a]' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
        {/* Overview */}
        <section id="wc-overview" className="mb-16 scroll-mt-16">
          <div className="border-l-2 border-[#c41e3a] pl-6 mb-10">
            <p className="text-lg text-zinc-300 leading-relaxed">If Wolverine's mutant healing factor were a peptide protocol, it would be BPC-157 + TB-500. One heals locally. The other heals systemically. Together, they create the most powerful recovery stack available to athletes.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {[
              { num: '2', title: 'Peptides', sub: 'Synergistic combination' },
              { num: '100+', title: 'Studies', sub: 'BPC-157 research base' },
              { num: '1-2', title: 'Weeks', sub: 'To noticeable results' },
              { num: '4-8', title: 'Week Cycle', sub: 'Standard protocol' },
            ].map((s, i) => (
              <div key={s.title} className="border border-zinc-800 p-4 hover:border-[#c41e3a]/20 transition-colors" data-testid={`stat-${i}`}>
                <p className="text-3xl font-heading text-[#c41e3a] leading-none mb-2">{s.num}</p>
                <p className="text-xs font-semibold text-white mb-1">{s.title}</p>
                <p className="text-[10px] text-zinc-500">{s.sub}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">Before starting any peptide protocol, get your <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide" className="text-[#c41e3a] hover:underline">baseline bloodwork done</Link>. If you're new to peptides, start with our <Link to="/blogs/article/the-complete-guide-to-peptide-therapy-for-athletes" className="text-[#c41e3a] hover:underline">complete peptide therapy guide</Link>.</p>
        </section>

        {/* What Is It */}
        <section id="wc-what" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHAT IS THE WOLVERINE COMPLEX?</h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-4">The Wolverine Complex combines two complementary peptides that target tissue repair through different mechanisms:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <div className="border border-green-900/50 bg-green-950/10 p-5">
              <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-green-400" /><span className="text-sm font-semibold text-white">BPC-157: Local Healer</span></div>
              <p className="text-xs text-zinc-400">Targets specific injury sites. Gut healing, tendon repair, anti-inflammatory. Works where you inject it.</p>
            </div>
            <div className="border border-blue-900/50 bg-blue-950/10 p-5">
              <div className="flex items-center gap-2 mb-2"><Heart className="w-4 h-4 text-blue-400" /><span className="text-sm font-semibold text-white">TB-500: Systemic Healer</span></div>
              <p className="text-xs text-zinc-400">Promotes repair throughout the entire body. Reduces systemic inflammation, improves flexibility, enhances cell migration.</p>
            </div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">Together, you get <strong className="text-white">targeted + systemic healing</strong> — addressing both the specific injury and the body's overall recovery capacity.</p>
        </section>

        {/* BPC-157 Deep Dive */}
        <section id="wc-bpc157" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">BPC-157 DEEP DIVE</h2>
          <PeptideCard data={PEPTIDE_DATA.bpc157} isOpen={!!openCards.bpc157} onToggle={() => setOpenCards(p => ({ ...p, bpc157: !p.bpc157 }))} />
          <p className="text-sm text-zinc-400 mt-4">For detailed dosing protocols, see our <Link to="/blogs/article/bpc-157-dosage-guide" className="text-[#c41e3a] hover:underline">BPC-157 dosage guide</Link>.</p>
        </section>

        {/* TB-500 Deep Dive */}
        <section id="wc-tb500" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">TB-500 DEEP DIVE</h2>
          <PeptideCard data={PEPTIDE_DATA.tb500} isOpen={!!openCards.tb500} onToggle={() => setOpenCards(p => ({ ...p, tb500: !p.tb500 }))} />
        </section>

        {/* Synergy */}
        <section id="wc-synergy" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHY THEY WORK BETTER TOGETHER</h2>
          <div className="space-y-3 mb-6">
            {[
              { title: 'Complementary Mechanisms', desc: 'BPC-157 promotes angiogenesis (new blood vessel formation) while TB-500 upregulates actin for cell migration. New blood vessels + enhanced cell movement = faster healing.' },
              { title: 'Local + Systemic Coverage', desc: 'BPC-157 concentrates healing at the injection site. TB-500 circulates throughout the body. You get precision targeting AND full-body recovery support.' },
              { title: 'Inflammation Reduction', desc: 'Both peptides reduce inflammation through different pathways. BPC-157 modulates nitric oxide. TB-500 reduces pro-inflammatory cytokines. Double the anti-inflammatory action.' },
              { title: 'Amplified Results', desc: 'Users consistently report faster recovery timelines when stacking both peptides compared to using either alone. The combination appears to create a synergistic healing response.' },
            ].map((item, i) => (
              <div key={item.title} className="flex gap-4 p-4 border border-zinc-800 hover:border-[#c41e3a]/20 transition-colors" data-testid={`synergy-${i}`}>
                <span className="text-2xl font-heading text-[#c41e3a] flex-shrink-0 w-8">{String(i + 1).padStart(2, '0')}</span>
                <div><p className="text-sm font-semibold text-white mb-1">{item.title}</p><p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* Protocol */}
        <section id="wc-protocol" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">THE PROTOCOL</h2>
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-3">8-Week Wolverine Complex Protocol</p>
          <div className="border border-zinc-800 overflow-hidden mb-6" data-testid="protocol-table">
            <div className="grid grid-cols-4 bg-zinc-900 border-b border-zinc-800">
              {['Phase', 'BPC-157', 'TB-500', 'Notes'].map(h => (
                <div key={h} className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#c41e3a]">{h}</div>
              ))}
            </div>
            {PROTOCOL_PHASES.map((row, i) => (
              <div key={row.phase} className={`grid grid-cols-4 border-b border-zinc-800/50 ${i % 2 ? 'bg-zinc-900/30' : ''}`}>
                <div className="p-3 text-xs text-white font-semibold">{row.phase}</div>
                <div className="p-3 text-xs text-zinc-400">{row.bpc}</div>
                <div className="p-3 text-xs text-zinc-400">{row.tb}</div>
                <div className="p-3 text-xs text-zinc-500">{row.note}</div>
              </div>
            ))}
          </div>
          <div className="bg-amber-950/20 border border-amber-700/30 p-4">
            <p className="text-xs text-amber-200/80"><strong className="text-amber-200">Essential:</strong> Learn to <Link to="/blogs/article/how-to-reconstitute-peptides" className="text-[#c41e3a] hover:underline">reconstitute peptides properly</Link> before starting. This is the #1 mistake beginners make.</p>
          </div>
        </section>

        {/* Side Effects */}
        <section id="wc-sideeffects" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">SIDE EFFECTS & SAFETY</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="border border-zinc-800 p-5">
              <div className="flex items-center gap-2 mb-3"><Info className="w-4 h-4 text-yellow-500" /><p className="text-xs font-semibold text-white">Common (Mild)</p></div>
              <ul className="space-y-1.5 text-xs text-zinc-400">
                <li>Injection site redness/irritation</li>
                <li>Mild nausea (BPC-157, usually passes)</li>
                <li>Lightheadedness (first few doses)</li>
                <li>Warmth at injection site</li>
              </ul>
            </div>
            <div className="border border-red-900/30 p-5">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-red-500" /><p className="text-xs font-semibold text-white">When to Stop</p></div>
              <ul className="space-y-1.5 text-xs text-zinc-400">
                <li>Persistent swelling at injection site</li>
                <li>Allergic reaction (rash, difficulty breathing)</li>
                <li>Unusual fatigue lasting more than a few days</li>
                <li>Any symptoms that feel "off" — trust your body</li>
              </ul>
            </div>
          </div>
          <p className="text-sm text-zinc-400">For a comprehensive analysis, read our <Link to="/blogs/article/peptide-side-effects-what-the-research-actually-says" className="text-[#c41e3a] hover:underline">interactive peptide side effects guide</Link>.</p>
        </section>

        {/* Advanced Stacking */}
        <section id="wc-stacking" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">ADVANCED STACKING OPTIONS</h2>
          <p className="text-sm text-zinc-400 mb-4">Once you've dialed in the base Wolverine Complex, consider adding a third peptide based on your goals:</p>
          <div className="space-y-3">
            {STACK_OPTIONS.map((opt, i) => (
              <div key={opt.name} className="border border-zinc-800 p-5 hover:border-[#c41e3a]/20 transition-colors" data-testid={`stack-option-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{opt.name}</span>
                  <span className="text-[10px] font-mono text-[#c41e3a] bg-[#c41e3a]/10 border border-[#c41e3a]/20 px-2 py-0.5">+ {opt.add}</span>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed mb-2">{opt.why}</p>
                <Link to={opt.link} className="text-xs text-[#c41e3a] hover:underline inline-flex items-center gap-1">Learn more <ExternalLink className="w-3 h-3" /></Link>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-zinc-900/30 border border-zinc-800/50 p-4">
            <p className="text-xs text-zinc-400">Build your personalized stack with our <Link to="/tools/peptide-stack-builder" className="text-[#c41e3a] hover:underline font-semibold">interactive Peptide Stack Builder</Link>.</p>
          </div>
        </section>

        {/* FAQ */}
        <section id="wc-faq" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">FREQUENTLY ASKED QUESTIONS</h2>
          <div>{FAQS.map((f, i) => (<FAQItem key={f.q} faq={f} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />))}</div>
        </section>

        {/* Related */}
        <div className="bg-zinc-900/30 border border-zinc-800/50 p-6 mb-16">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-4">Continue Your Research</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {RELATED.map((r, i) => (<Link key={r.title} to={r.href} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#c41e3a] transition-colors py-1"><span className="text-[#c41e3a] text-xs">&#8594;</span> {r.title}</Link>))}
          </div>
        </div>

        {/* CTA */}
        <div className="border border-[#c41e3a]/30 bg-[#c41e3a]/[0.03] p-6 text-center mb-16">
          <h3 className="font-heading text-2xl text-white mb-3">READY TO START?</h3>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/tools/peptide-stack-builder" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#c41e3a] text-white text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#a01830] transition-colors" data-testid="cta-stack-builder"><Target className="w-3.5 h-3.5" /> Build Your Stack</Link>
            <Link to="/tools/peptide-calculator" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs font-mono font-bold uppercase tracking-wider hover:border-[#c41e3a]/40 hover:text-[#c41e3a] transition-colors" data-testid="cta-calculator"><Beaker className="w-3.5 h-3.5" /> Dosage Calculator</Link>
          </div>
        </div>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        {/* Disclaimer */}
        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Disclaimer:</strong> This content is for informational purposes only. Peptides are research chemicals and are not approved by the FDA for human use. Always consult with a healthcare provider before starting any new supplement protocol. Individual results may vary.
        </div>
      </main>

      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 w-10 h-10 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center text-white hover:bg-[#c41e3a] hover:text-white transition-colors z-50" data-testid="scroll-to-top"><ArrowUp className="w-4 h-4" /></button>
      )}
    </div>
  );
}
